// src/renderer/components/common/ProgressBar.tsx

import React from 'react';
import { cn } from '../../utils/cn';

export interface ProgressBarProps {
  /** Progress value from 0-100 */
  progress: number;
  /** Label to display above the progress bar */
  label?: string;
  /** Whether to show the percentage text */
  showPercentage?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Color variant */
  variant?: 'default' | 'success' | 'warning' | 'error';
  /** Test ID for the component */
  'data-testid'?: string;
}

const sizeStyles = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
};

const variantStyles = {
  default: 'bg-blue-500',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
};

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  label,
  showPercentage = true,
  className = '',
  size = 'md',
  variant = 'default',
  'data-testid': testId = 'progress-bar',
}) => {
  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className={cn('w-full', className)} data-testid={testId}>
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-1">
          {label && (
            <span
              className="text-sm text-zinc-300"
              data-testid="progress-label"
            >
              {label}
            </span>
          )}
          {showPercentage && (
            <span
              className="text-sm text-zinc-400"
              data-testid="progress-percentage"
              aria-hidden="true"
            >
              {Math.round(clampedProgress)}%
            </span>
          )}
        </div>
      )}
      <div
        className={cn(
          'w-full bg-zinc-700 rounded-full overflow-hidden',
          sizeStyles[size]
        )}
        data-testid="progress-track"
      >
        <div
          className={cn(
            'h-full transition-all duration-200 ease-out',
            variantStyles[variant]
          )}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label ? `${label}: ${Math.round(clampedProgress)}%` : `Progress: ${Math.round(clampedProgress)}%`}
          data-testid="progress-fill"
        />
      </div>
    </div>
  );
};

export default ProgressBar;
